import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatCurrency } from '@/lib/utils';
import type { ContractTemplate } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Percent, DollarSign, Clock, Users, Eye, EyeOff, Trash2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/components/ui/toast';

export default function MyTemplatesPage() {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['my-templates'],
        queryFn: () => api.get<{ data: ContractTemplate[] }>('/my-templates'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/templates/${id}`),
        onSuccess: () => {
            trackEvent('template_deleted');
            toast('Template deleted.', 'success');
            setConfirmDelete(null);
            queryClient.invalidateQueries({ queryKey: ['my-templates'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const templates = data?.data ?? [];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">My Templates</h1>
                    <p className="text-sm text-slate-400">Manage your contract templates</p>
                </div>
                <Link to="/templates/create">
                    <Button size="sm">
                        <Plus className="mr-1 h-4 w-4" />
                        New Template
                    </Button>
                </Link>
            </div>

            {isLoading ? (
                <p className="text-sm text-slate-400">Loading templates...</p>
            ) : templates.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <p className="text-sm text-slate-400 mb-4">You haven't created any templates yet.</p>
                        <Link to="/templates/create">
                            <Button>
                                <Plus className="mr-1 h-4 w-4" />
                                Create Your First Template
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {templates.map((template) => (
                        <Card key={template.id}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Link
                                                to={`/marketplace/${template.id}`}
                                                className="font-medium text-sm hover:text-violet-400 transition-colors truncate"
                                            >
                                                {template.title}
                                            </Link>
                                            {template.is_published ? (
                                                <Badge className="bg-green-500/20 text-green-400 text-[10px] shrink-0">
                                                    <Eye className="mr-0.5 h-2.5 w-2.5" />
                                                    Published
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-slate-700 text-slate-400 text-[10px] shrink-0">
                                                    <EyeOff className="mr-0.5 h-2.5 w-2.5" />
                                                    Draft
                                                </Badge>
                                            )}
                                            {template.hardcore_mode && (
                                                <Badge variant="destructive" className="text-[10px] shrink-0">Hardcore</Badge>
                                            )}
                                        </div>
                                        {template.description && (
                                            <p className="text-xs text-slate-400 line-clamp-1 mb-2">{template.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                            <span className="flex items-center gap-1">
                                                <DollarSign className="h-3 w-3" />
                                                {formatCurrency(template.opening_balance)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Percent className="h-3 w-3" />
                                                {template.interest_rate}% APR
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {template.compounding_frequency}
                                            </span>
                                            {template.contracts_count !== undefined && (
                                                <span className="flex items-center gap-1">
                                                    <Users className="h-3 w-3" />
                                                    {template.contracts_count} signed
                                                </span>
                                            )}
                                        </div>
                                        {template.tags && template.tags.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {template.tags.map((tag) => (
                                                    <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 gap-1">
                                        {confirmDelete === template.id ? (
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => deleteMutation.mutate(template.id)}
                                                    disabled={deleteMutation.isPending}
                                                >
                                                    {deleteMutation.isPending ? '...' : 'Delete'}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setConfirmDelete(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <Link to={`/templates/${template.id}/edit`}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-slate-400 hover:text-slate-200 h-8 w-8 p-0"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                                    onClick={() => setConfirmDelete(template.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
